import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Film, UploadCloud, Volume2, VolumeX, Play, Pause, Scissors, Download,
  RefreshCw, FileVideo, Sliders, Camera, Stamp, Maximize2, Plus, Trash2,
  ZoomIn, ZoomOut, Palette, SkipBack, SkipForward, FolderOpen,
} from 'lucide-react';
import { AudioTrackLayer, VideoClipSegment, VideoSource, SelectedMediaTarget } from './types';
import { TimelinePlayhead } from './TimelinePlayhead';
import { AudioTrackLane } from './AudioTrackLane';
import { VideoTrackLane } from './VideoTrackLane';

export function VideoEditor() {
  // Video sources (each uploaded file)
  const [videoSources, setVideoSources] = useState<VideoSource[]>([]);
  // Hidden video elements per source for seamless switching
  const videoRefsMap = useRef<Record<string, HTMLVideoElement>>({});

  // The currently-active visible player
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeSourceId, setActiveSourceId] = useState<string | null>(null);

  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const [videoSegments, setVideoSegments] = useState<VideoClipSegment[]>([]);
  const [audioLayers, setAudioLayers] = useState<AudioTrackLayer[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<SelectedMediaTarget>(null);

  const [originalAudioMuted, setOriginalAudioMuted] = useState<boolean>(false);
  const [originalAudioVolume, setOriginalAudioVolume] = useState<number>(100);

  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<'files' | 'preset' | 'effects' | 'text'>('files');
  const [targetPreset, setTargetPreset] = useState<'1080p' | '720p' | '480p' | '9:16' | '1:1'>('720p');
  const [brightness, setBrightness] = useState<number>(100);
  const [contrast, setContrast] = useState<number>(100);
  const [saturation, setSaturation] = useState<number>(100);
  const [grayscale, setGrayscale] = useState<boolean>(false);
  const [watermarkText, setWatermarkText] = useState<string>('');
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-right' | 'center' | 'bottom-left' | 'bottom-right'>('bottom-right');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number>(0);

  const timelineRef = useRef<HTMLDivElement>(null);

  // Total sequence duration
  const totalSequenceDuration = videoSegments.reduce(
    (acc, seg) => acc + Math.max(0, seg.endTime - seg.startTime), 0
  );

  // ─── Video volume sync (bypass React muted prop bug) ───────────────────────
  useEffect(() => {
    if (!videoRef.current) return;
    const vid = videoRef.current;
    const vol = originalAudioMuted ? 0 : Math.max(0, Math.min(1, originalAudioVolume / 100));
    vid.volume = vol;
    vid.muted = originalAudioMuted || originalAudioVolume === 0;
  }, [originalAudioVolume, originalAudioMuted]);

  // ─── Audio layer volume sync ────────────────────────────────────────────────
  useEffect(() => {
    audioLayers.forEach((layer) => {
      if (layer.audioInstance) {
        layer.audioInstance.volume = layer.muted ? 0 : Math.max(0, Math.min(1, layer.volume / 100));
        layer.audioInstance.muted = layer.muted || layer.volume === 0;
      }
    });
  }, [audioLayers]);

  // ─── Sequence time helpers ──────────────────────────────────────────────────
  const getSourceTimeForSequenceTime = useCallback(
    (seqTime: number): { sourceTime: number; segmentIndex: number; sourceId: string } => {
      let accum = 0;
      for (let i = 0; i < videoSegments.length; i++) {
        const seg = videoSegments[i];
        const segDur = Math.max(0, seg.endTime - seg.startTime);
        if (seqTime <= accum + segDur) {
          return { sourceTime: seg.startTime + (seqTime - accum), segmentIndex: i, sourceId: seg.sourceId };
        }
        accum += segDur;
      }
      const last = videoSegments[videoSegments.length - 1];
      return { sourceTime: last?.endTime ?? 0, segmentIndex: Math.max(0, videoSegments.length - 1), sourceId: last?.sourceId ?? '' };
    },
    [videoSegments]
  );

  const getSequenceTimeFromSourceTime = useCallback(
    (srcTime: number): number => {
      if (!activeSourceId) return 0;
      let accum = 0;
      for (let i = 0; i < videoSegments.length; i++) {
        const seg = videoSegments[i];
        if (seg.sourceId !== activeSourceId) { accum += seg.endTime - seg.startTime; continue; }
        const segDur = Math.max(0, seg.endTime - seg.startTime);
        if (srcTime >= seg.startTime && srcTime <= seg.endTime) {
          return accum + (srcTime - seg.startTime);
        }
        accum += segDur;
      }
      return accum;
    },
    [videoSegments, activeSourceId]
  );

  // ─── Audio sync interval ────────────────────────────────────────────────────
  const audioSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioLayersRef = useRef(audioLayers);
  useEffect(() => { audioLayersRef.current = audioLayers; }, [audioLayers]);
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const startAudioSyncInterval = () => {
    if (audioSyncIntervalRef.current) clearInterval(audioSyncIntervalRef.current);
    audioSyncIntervalRef.current = setInterval(() => {
      if (!isPlayingRef.current || !videoRef.current) return;
      const seqTime = videoRef.current.currentTime; // approximate
      audioLayersRef.current.forEach((layer) => {
        if (!layer.audioInstance) return;
        const relTime = seqTime - layer.startOffset;
        const inst = layer.audioInstance;
        if (relTime >= 0 && relTime <= (layer.duration - layer.trimStart) && !layer.muted && layer.volume > 0) {
          const targetAudioTime = layer.trimStart + relTime;
          const drift = Math.abs(inst.currentTime - targetAudioTime);
          if (drift > 0.3 && !inst.paused) inst.currentTime = targetAudioTime;
          if (inst.paused) {
            inst.currentTime = targetAudioTime;
            inst.volume = Math.max(0, Math.min(1, layer.volume / 100));
            inst.play().catch(() => {});
          }
        } else if (relTime < 0 || relTime > (layer.duration - layer.trimStart)) {
          if (!inst.paused) inst.pause();
        }
      });
    }, 100);
  };

  const stopAudioSyncInterval = () => {
    if (audioSyncIntervalRef.current) { clearInterval(audioSyncIntervalRef.current); audioSyncIntervalRef.current = null; }
  };

  useEffect(() => () => stopAudioSyncInterval(), []);

  // ─── Upload first/additional video ─────────────────────────────────────────
  const loadVideoSource = (file: File): Promise<VideoSource> => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const tmp = document.createElement('video');
      tmp.src = url;
      tmp.onloadedmetadata = () => {
        resolve({ id: `src-${Date.now()}-${Math.random().toString(36).slice(2)}`, file, url, duration: tmp.duration, name: file.name });
      };
    });
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Clear everything
    videoSources.forEach(s => URL.revokeObjectURL(s.url));
    audioLayers.forEach(l => { l.audioInstance.pause(); URL.revokeObjectURL(l.url); });
    stopAudioSyncInterval();

    const src = await loadVideoSource(file);
    setVideoSources([src]);
    setActiveSourceId(src.id);
    setVideoSegments([{ id: `seg-${Date.now()}`, sourceId: src.id, startTime: 0, endTime: src.duration, sourceStart: 0 }]);
    setAudioLayers([]);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(src.duration);
    setSelectedMedia(null);

    if (videoRef.current) {
      videoRef.current.src = src.url;
      videoRef.current.load();
    }
  };

  const handleAddVideoClip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = await loadVideoSource(file);
    setVideoSources(prev => [...prev, src]);
    // Append a new full-length segment for this source
    setVideoSegments(prev => [...prev, {
      id: `seg-${Date.now()}`,
      sourceId: src.id,
      startTime: 0,
      endTime: src.duration,
      sourceStart: 0,
    }]);
  };

  // ─── Audio layer handlers ───────────────────────────────────────────────────
  const handleAddAudioLayer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const inst = new Audio(url);
    inst.onerror = (err) => console.error('[AUDIO] load error for', file.name, inst.error?.code);
    inst.onloadedmetadata = () => {
      const newLayer: AudioTrackLayer = {
        id: `audio-${Date.now()}`,
        name: file.name, file, url,
        audioInstance: inst,
        volume: 80, muted: false,
        startOffset: Math.round(currentTime),
        trimStart: 0,
        duration: inst.duration || 10,
      };
      setAudioLayers(prev => [...prev, newLayer]);
      setSelectedMedia({ type: 'audio', id: newLayer.id });
    };
  };

  const handleRemoveAudioLayer = (id: string) => {
    setAudioLayers(prev => {
      const l = prev.find(x => x.id === id);
      if (l) { l.audioInstance.pause(); URL.revokeObjectURL(l.url); }
      return prev.filter(x => x.id !== id);
    });
    if (selectedMedia?.id === id) setSelectedMedia(null);
  };

  const handleAudioVolumeChange = (id: string, volume: number) => {
    setAudioLayers(prev => prev.map(l => {
      if (l.id !== id) return l;
      l.audioInstance.volume = l.muted ? 0 : Math.max(0, Math.min(1, volume / 100));
      return { ...l, volume };
    }));
  };

  const handleToggleAudioMute = (id: string) => {
    setAudioLayers(prev => prev.map(l => {
      if (l.id !== id) return l;
      const newMuted = !l.muted;
      l.audioInstance.muted = newMuted;
      return { ...l, muted: newMuted };
    }));
  };

  const handleAudioOffsetChange = (id: string, startOffset: number) =>
    setAudioLayers(prev => prev.map(l => l.id === id ? { ...l, startOffset } : l));

  const handleAudioTrimStartChange = (id: string, trimStart: number) => {
    setAudioLayers(prev => prev.map(l => {
      if (l.id !== id) return l;
      // Also reset audio position if it was playing
      if (!l.audioInstance.paused) {
        const relTime = currentTime - l.startOffset;
        l.audioInstance.currentTime = trimStart + Math.max(0, relTime);
      }
      return { ...l, trimStart };
    }));
  };

  // ─── Split selected media at playhead ──────────────────────────────────────
  const handleSplitSelectedMedia = () => {
    if (currentTime <= 0 || currentTime >= totalSequenceDuration) return;

    if (selectedMedia?.type === 'audio') {
      const target = audioLayers.find(a => a.id === selectedMedia.id);
      if (!target) return;
      const effectiveDur = target.duration - target.trimStart;
      const relSplit = currentTime - target.startOffset;
      if (relSplit <= 0 || relSplit >= effectiveDur) return;

      const rightInst = new Audio(target.url);
      const left: AudioTrackLayer = { ...target, id: `audio-${Date.now()}-a` };
      const right: AudioTrackLayer = {
        ...target, id: `audio-${Date.now()}-b`,
        audioInstance: rightInst,
        startOffset: currentTime,
        trimStart: target.trimStart + relSplit,
      };
      setAudioLayers(prev => prev.flatMap(l => l.id === target.id ? [left, right] : [l]));
      return;
    }

    // Split video segment
    let accum = 0;
    for (let i = 0; i < videoSegments.length; i++) {
      const seg = videoSegments[i];
      const segDur = seg.endTime - seg.startTime;
      if (currentTime > accum && currentTime < accum + segDur) {
        const splitSrc = seg.startTime + (currentTime - accum);
        setVideoSegments(prev => {
          const updated = [...prev];
          updated.splice(i, 1,
            { id: `seg-${Date.now()}-a`, sourceId: seg.sourceId, startTime: seg.startTime, endTime: splitSrc, sourceStart: seg.sourceStart },
            { id: `seg-${Date.now()}-b`, sourceId: seg.sourceId, startTime: splitSrc, endTime: seg.endTime, sourceStart: splitSrc }
          );
          return updated;
        });
        return;
      }
      accum += segDur;
    }
  };

  const handleRemoveSegment = (id: string) => {
    if (videoSegments.length <= 1) return;
    setVideoSegments(prev => prev.filter(seg => seg.id !== id));
    if (selectedMedia?.id === id) setSelectedMedia(null);
  };

  const moveSegment = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || toIdx >= videoSegments.length) return;
    setVideoSegments(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  // ─── Playback control ───────────────────────────────────────────────────────
  // Switch active source and update video src when needed
  const switchToSource = async (sourceId: string, seekTo: number) => {
    if (!videoRef.current) return;
    const src = videoSources.find(s => s.id === sourceId);
    if (!src) return;
    if (activeSourceId !== sourceId || !videoRef.current.src || videoRef.current.src !== src.url) {
      setActiveSourceId(sourceId);
      videoRef.current.src = src.url;
      await new Promise(r => {
        const v = videoRef.current;
        if (!v || v.readyState >= 2) return r(null);
        v.oncanplay = () => {
          v.oncanplay = null;
          r(null);
        };
        v.load();
      });
    }
    videoRef.current.currentTime = seekTo;
  };

  const togglePlay = async () => {
    if (!videoRef.current || videoSegments.length === 0) return;
    try {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        stopAudioSyncInterval();
        audioLayers.forEach(l => l.audioInstance.pause());
        return;
      }

      // Seek to start if at end
      let seekSeqTime = currentTime;
      if (currentTime >= totalSequenceDuration - 0.1) {
        seekSeqTime = 0;
        setCurrentTime(0);
      }
      const { sourceTime, sourceId } = getSourceTimeForSequenceTime(seekSeqTime);
      await switchToSource(sourceId, sourceTime);

      // Unlock + position audio layers inside gesture
      await Promise.all(audioLayers.map(async (layer) => {
        try {
          const inst = layer.audioInstance;
          const savedVol = inst.volume;
          inst.volume = 0;
          await inst.play();
          inst.pause();
          inst.volume = savedVol;
        } catch { }
      }));

      await videoRef.current.play();
      setIsPlaying(true);

      // Start audio that's already in range
      audioLayers.forEach(layer => {
        const relTime = seekSeqTime - layer.startOffset;
        const effectiveDur = layer.duration - layer.trimStart;
        if (relTime >= 0 && relTime <= effectiveDur && !layer.muted && layer.volume > 0) {
          const inst = layer.audioInstance;
          inst.currentTime = layer.trimStart + relTime;
          inst.volume = Math.max(0, Math.min(1, layer.volume / 100));
          inst.play().catch(() => { });
        }
      });

      startAudioSyncInterval();
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('Playback error:', err);
      }
    }
  };

  const handleSeek = (seqTime: number) => {
    setCurrentTime(seqTime);
    if (videoRef.current && videoSegments.length > 0) {
      const { sourceTime, sourceId } = getSourceTimeForSequenceTime(seqTime);
      // Only switch src if source changed
      if (sourceId !== activeSourceId) {
        const src = videoSources.find(s => s.id === sourceId);
        if (src) { videoRef.current.src = src.url; setActiveSourceId(sourceId); }
      }
      videoRef.current.currentTime = sourceTime;
    }
    audioLayers.forEach(layer => {
      const relTime = seqTime - layer.startOffset;
      const effectiveDur = layer.duration - layer.trimStart;
      if (relTime >= 0 && relTime <= effectiveDur) {
        layer.audioInstance.currentTime = layer.trimStart + relTime;
      } else {
        layer.audioInstance.pause();
      }
    });
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current || totalSequenceDuration <= 0) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    handleSeek(pct * totalSequenceDuration);
  };

  const captureFrame = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const c = document.createElement('canvas');
    c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
    c.getContext('2d')?.drawImage(v, 0, 0, c.width, c.height);
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `frame_${Math.round(currentTime)}s.png`;
    a.click();
  };

  const handleStartOver = () => {
    stopAudioSyncInterval();
    videoSources.forEach(s => URL.revokeObjectURL(s.url));
    audioLayers.forEach(l => { l.audioInstance.pause(); URL.revokeObjectURL(l.url); });
    setVideoSources([]);
    setVideoSegments([]);
    setAudioLayers([]);
    setActiveSourceId(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (videoRef.current) { videoRef.current.src = ''; }
  };

  const formatTime = (secs: number) => {
    if (isNaN(secs) || secs < 0) return '0:00';
    const m = Math.floor(secs / 60), s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const hasVideo = videoSources.length > 0;
  const activeSource = videoSources.find(s => s.id === activeSourceId);

  // Keep videoRef.current.src in sync with activeSource
  useEffect(() => {
    if (videoRef.current && activeSource?.url) {
      if (videoRef.current.src !== activeSource.url) {
        videoRef.current.src = activeSource.url;
        videoRef.current.volume = originalAudioMuted ? 0 : Math.max(0, Math.min(1, originalAudioVolume / 100));
        videoRef.current.muted = originalAudioMuted || originalAudioVolume === 0;
        videoRef.current.load();
      }
    }
  }, [activeSourceId, activeSource, originalAudioMuted, originalAudioVolume]);

  return (
    <div className="space-y-4">
      {!hasVideo ? (
        /* Upload Dropzone */
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/40 dark:border-slate-800 shadow-2xl text-center space-y-6 max-w-3xl mx-auto">
          <div className="p-5 bg-gradient-to-tr from-purple-600 via-indigo-600 to-pink-500 text-white rounded-3xl w-20 h-20 mx-auto flex items-center justify-center shadow-xl shadow-indigo-500/30">
            <Film className="w-10 h-10" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">Blivix Video Studio</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              Multi-clip timeline editor. Stack clips, trim audio sources, drag-to-move background music tracks.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-sm rounded-2xl shadow-xl shadow-indigo-500/30 cursor-pointer transition-all hover:scale-105">
            <UploadCloud className="w-5 h-5" />
            <span>Select First Video</span>
            <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
          </label>
        </div>
      ) : (
        /* Studio Workbench */
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col">
          {/* Menu Bar */}
          <div className="bg-slate-100 dark:bg-slate-950 px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-indigo-600 rounded-lg text-white"><Film className="w-4 h-4" /></div>
              <span className="font-black text-sm text-slate-900 dark:text-white">Blivix Studio
                <span className="text-indigo-600 dark:text-indigo-400 font-normal ml-2">{videoSources.length} clip{videoSources.length !== 1 ? 's' : ''}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={captureFrame}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" /><span>Snap Frame</span>
              </button>
              <button type="button" onClick={handleStartOver}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" />New Project
              </button>
            </div>
          </div>

          {/* Top Split */}
          <div className="grid grid-cols-1 xl:grid-cols-12 border-b border-slate-200 dark:border-slate-800 min-h-[380px]">
            {/* Left Dock */}
            <div className="xl:col-span-5 border-r border-slate-200 dark:border-slate-800 flex flex-col bg-slate-50/80 dark:bg-slate-950/60">
              <div className="flex bg-slate-100 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 p-1">
                {[
                  { id: 'files', label: '📂 Files' },
                  { id: 'preset', label: '📐 Format' },
                  { id: 'effects', label: '🎨 Effects' },
                  { id: 'text', label: '🏷️ Text' },
                ].map(tab => (
                  <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id as any)}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                      activeTab === tab.id ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Files Tab */}
              {activeTab === 'files' && (
                <div className="p-4 space-y-4 flex-1 overflow-auto">
                  {/* Video Sources */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Video Clips ({videoSources.length})</span>
                      <label className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /><span>+ Add Clip</span>
                        <input type="file" accept="video/*" className="hidden" onChange={handleAddVideoClip} />
                      </label>
                    </div>
                    {videoSources.map((src, idx) => (
                      <div key={src.id} className="p-2.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                          <FileVideo className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">{src.name}</p>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">{formatTime(src.duration)} • {(src.file.size / (1024 * 1024)).toFixed(1)}MB</p>
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400 font-bold border border-indigo-200 dark:border-indigo-800 flex-shrink-0">
                          #{idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-slate-200 dark:border-slate-800 pt-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Background Music ({audioLayers.length})</span>
                      <label className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /><span>+ Add Music</span>
                        <input type="file" accept="audio/*" className="hidden" onChange={handleAddAudioLayer} />
                      </label>
                    </div>
                    {audioLayers.map((layer, idx) => (
                      <div key={layer.id} className="p-2.5 bg-purple-50 dark:bg-purple-950/40 rounded-xl border border-purple-200 dark:border-purple-800/60 flex items-center justify-between">
                        <span className="text-xs font-bold text-purple-900 dark:text-purple-200 truncate max-w-[200px]">{layer.name}</span>
                        <button type="button" onClick={() => handleRemoveAudioLayer(layer.id)} className="text-red-500 hover:text-red-600 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'preset' && (
                <div className="p-4 space-y-3 flex-1 overflow-auto">
                  <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Output Format</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: '1080p', label: '1080p Full HD', desc: '1920×1080' },
                      { id: '720p', label: '720p HD', desc: '1280×720' },
                      { id: '480p', label: '480p SD', desc: '854×480' },
                      { id: '9:16', label: '📱 9:16 Reels', desc: '1080×1920' },
                      { id: '1:1', label: '⬛ 1:1 Square', desc: '1080×1080' },
                    ].map(p => (
                      <button key={p.id} type="button" onClick={() => setTargetPreset(p.id as any)}
                        className={`p-3 rounded-xl text-left border transition-all ${
                          targetPreset === p.id ? 'bg-indigo-600 text-white border-indigo-600 font-bold' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-indigo-500'}`}>
                        <p className="text-xs font-bold">{p.label}</p>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'effects' && (
                <div className="p-4 space-y-4 flex-1 overflow-auto">
                  <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Color Adjustments</h4>
                  {[
                    { label: 'Brightness', value: brightness, set: setBrightness },
                    { label: 'Contrast', value: contrast, set: setContrast },
                    { label: 'Saturation', value: saturation, set: setSaturation },
                  ].map(({ label, value, set }) => (
                    <div key={label} className="space-y-1">
                      <div className="flex justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                        <span>{label}</span><span className="font-mono text-indigo-600 dark:text-indigo-400">{value}%</span>
                      </div>
                      <input type="range" min="50" max="150" value={value}
                        onChange={e => set(Number(e.target.value))}
                        className="w-full accent-indigo-500 cursor-pointer" />
                    </div>
                  ))}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={grayscale} onChange={e => setGrayscale(e.target.checked)} className="accent-indigo-500" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Grayscale</span>
                  </label>
                </div>
              )}

              {activeTab === 'text' && (
                <div className="p-4 space-y-4 flex-1 overflow-auto">
                  <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Text Watermark</h4>
                  <input type="text" placeholder="e.g. @MyBrandHandle" value={watermarkText}
                    onChange={e => setWatermarkText(e.target.value)}
                    className="w-full px-3.5 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  <div className="grid grid-cols-3 gap-2">
                    {['top-left','top-right','center','bottom-left','bottom-right'].map(pos => (
                      <button key={pos} type="button" onClick={() => setWatermarkPosition(pos as any)}
                        className={`py-1.5 text-[10px] font-bold rounded-lg border transition-all ${
                          watermarkPosition === pos ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400'}`}>
                        {pos.replace('-', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Monitor */}
            <div className="xl:col-span-7 p-4 bg-slate-900 dark:bg-black/90 flex flex-col justify-between items-center space-y-3">
              <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden border border-slate-800 flex items-center justify-center shadow-2xl">
                <video
                  ref={videoRef}
                  src={activeSource?.url || undefined}
                  className="w-full h-full object-contain cursor-pointer"
                  playsInline
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.volume = originalAudioMuted ? 0 : Math.max(0, Math.min(1, originalAudioVolume / 100));
                      videoRef.current.muted = originalAudioMuted || originalAudioVolume === 0;
                    }
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => { setIsPlaying(false); stopAudioSyncInterval(); audioLayers.forEach(l => l.audioInstance.pause()); }}
                  onClick={togglePlay}
                  style={{ filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) ${grayscale ? 'grayscale(100%)' : ''}` }}
                />
                {!isPlaying && (
                  <button onClick={togglePlay}
                    className="absolute p-5 bg-indigo-600/90 hover:bg-indigo-600 text-white rounded-full backdrop-blur-md transition-all hover:scale-110 shadow-2xl shadow-indigo-600/50">
                    <Play className="w-8 h-8 ml-1" />
                  </button>
                )}
                {watermarkText && (
                  <div className={`absolute p-2.5 font-bold text-white text-xs bg-black/50 backdrop-blur-sm rounded-lg pointer-events-none ${
                    watermarkPosition === 'top-left' ? 'top-4 left-4' :
                    watermarkPosition === 'top-right' ? 'top-4 right-4' :
                    watermarkPosition === 'center' ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' :
                    watermarkPosition === 'bottom-left' ? 'bottom-4 left-4' : 'bottom-4 right-4'}`}>
                    {watermarkText}
                  </div>
                )}
              </div>

              {/* Transport Bar */}
              <div className="w-full bg-slate-100 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between text-slate-900 dark:text-white">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => handleSeek(0)} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300 transition-colors" title="Skip to Start">
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={togglePlay} className="p-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-all">
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button type="button" onClick={() => handleSeek(totalSequenceDuration)} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300 transition-colors" title="Skip to End">
                    <SkipForward className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 ml-2">
                    {formatTime(currentTime)} / {formatTime(totalSequenceDuration)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => {}} disabled={isExporting}
                    className="px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-bold rounded-lg shadow flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /><span>Export</span>
                  </button>
                  <button type="button" onClick={() => videoRef.current?.requestFullscreen()} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300">
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Deck */}
          <div className="p-4 bg-slate-50 dark:bg-slate-950 space-y-3">
            <div className="flex justify-between items-center text-xs pb-2 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleSplitSelectedMedia}
                  className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow">
                  <Scissors className="w-3.5 h-3.5" />✂️ Split at Playhead
                </button>
                {selectedMedia && (
                  <span className="text-xs font-bold text-cyan-700 dark:text-cyan-400 px-2.5 py-0.5 rounded bg-cyan-100 dark:bg-cyan-950/80 border border-cyan-300 dark:border-cyan-800">
                    Selected: {selectedMedia.type.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500 dark:text-slate-400">Zoom:</span>
                <button type="button" onClick={() => setZoomLevel(z => Math.max(1, z - 0.5))} className="p-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400">{zoomLevel}x</span>
                <button type="button" onClick={() => setZoomLevel(z => Math.min(4, z + 0.5))} className="p-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Timeline with full-height playhead */}
            <div className="relative space-y-2 select-none">
              {/* Absolute playhead container spanning full height */}
              <div className="absolute top-0 bottom-0 left-0 right-0 z-40 pointer-events-none flex">
                <div className="mr-3 w-48 flex-shrink-0" />
                <div className="flex-1 relative" ref={timelineRef} onClick={handleTimelineClick} style={{ pointerEvents: 'all', cursor: 'crosshair' }}>
                  <TimelinePlayhead
                    videoRef={videoRef}
                    totalSequenceDuration={totalSequenceDuration}
                    isPlaying={isPlaying}
                    getSequenceTimeFromSourceTime={getSequenceTimeFromSourceTime}
                    onTimeSync={(seqTime) => setCurrentTime(seqTime)}
                  />
                </div>
              </div>

              {/* Ruler */}
              <div className="flex items-center">
                <div className="mr-3 w-48 flex-shrink-0 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider flex justify-between">
                  <span>Ruler</span><span>0:00</span>
                </div>
                <div className="flex-1 h-6 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center px-2 text-[10px] font-mono text-slate-600 dark:text-slate-400 justify-between overflow-hidden pointer-events-none">
                  {[0, 0.25, 0.5, 0.75, 1].map(f => (
                    <span key={f} className="font-bold">{formatTime(totalSequenceDuration * f)}</span>
                  ))}
                </div>
              </div>

              {/* Track Lanes */}
              <div className="space-y-2 pt-1">
                <VideoTrackLane
                  segments={videoSegments}
                  sources={videoSources}
                  totalSequenceDuration={totalSequenceDuration}
                  selectedMedia={selectedMedia}
                  onSelect={id => setSelectedMedia({ type: 'video', id })}
                  onMoveSegment={moveSegment}
                  onRemoveSegment={handleRemoveSegment}
                  formatTime={formatTime}
                />

                {/* Original Audio Track */}
                <div className="bg-white/90 dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-800 p-2 min-h-[50px] flex items-center">
                  <div className="mr-3 w-48 flex-shrink-0 flex items-center justify-between gap-1.5 bg-slate-100 dark:bg-slate-950 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800">
                    <button type="button" onClick={() => setOriginalAudioMuted(!originalAudioMuted)}
                      className={`p-1 rounded transition-colors ${originalAudioMuted ? 'bg-red-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400'}`}>
                      {originalAudioMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </button>
                    <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">Orig Vol</span>
                    <input type="range" min="0" max="200" value={originalAudioVolume}
                      onChange={e => setOriginalAudioVolume(Number(e.target.value))}
                      className="w-14 accent-emerald-500 cursor-pointer" />
                    <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 font-bold w-7 text-right">{originalAudioVolume}%</span>
                  </div>
                  <div className="flex-1 h-8 bg-emerald-50 dark:bg-emerald-950/60 rounded-lg border border-emerald-200 dark:border-emerald-800/60 flex items-center px-3 text-[10px] font-bold text-emerald-800 dark:text-emerald-300">
                    Original Audio ({originalAudioMuted ? 'MUTED' : `${originalAudioVolume}%`})
                  </div>
                </div>

                {/* Audio Track Lanes with trimStart */}
                {audioLayers.map((layer, idx) => (
                  <AudioTrackLane
                    key={layer.id}
                    layer={layer}
                    trackIndex={idx + 3}
                    totalSequenceDuration={totalSequenceDuration}
                    selectedMedia={selectedMedia}
                    onSelect={id => setSelectedMedia({ type: 'audio', id })}
                    onVolumeChange={handleAudioVolumeChange}
                    onToggleMute={handleToggleAudioMute}
                    onOffsetChange={handleAudioOffsetChange}
                    onTrimStartChange={handleAudioTrimStartChange}
                    onRemove={handleRemoveAudioLayer}
                    formatTime={formatTime}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
